import React from 'react';
import { RefreshCw, Search, Filter, BarChart3, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import Card from './Card';
import ErrorAlert from './ErrorAlert';
import LoadingSpinner from './LoadingSpinner';
import ActionButton from './ActionButton';

const DataTable = ({
  title,
  subtitle,
  icon: Icon,
  iconColor = 'text-blue-600',
  actions = [],
  filters = [],
  searchProps = null,
  onRefresh,
  toolbarActions = [],
  showStats = false,
  onToggleStats = null,
  loading = false,
  error = null,
  success = null,
  children,
  className = '',
  showFilters = true,
  filtersClassName = ''
}) => {
  if (loading) {
    return <LoadingSpinner center text="Loading..." />;
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <Card padding="none" rounded="xl">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {Icon && <Icon className={`w-6 h-6 ${iconColor}`} />}
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h2>
                {subtitle && <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {actions.map((action, index) => (
                <ActionButton
                  key={index}
                  icon={action.icon}
                  onClick={action.onClick}
                  variant={action.variant || 'primary'}
                  size={action.size || 'md'}
                  disabled={action.disabled}
                >
                  {action.label}
                </ActionButton>
              ))}
            </div>
          </div>
        </div>

        {showFilters && (searchProps || filters.length > 0 || onRefresh || toolbarActions.length > 0) && (
          <div className={`p-6 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${filtersClassName}`}>
            <div className="flex items-center gap-4">
              <div className="flex-1 flex items-center gap-4">
                {searchProps && (
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder={searchProps.placeholder || "Search..."}
                      value={searchProps.value}
                      onChange={searchProps.onChange}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}

                {filters.map((filter, index) => {
                  const FilterIcon = filter.icon || Filter;

                  if (filter.type === 'select') {
                    return (
                      <div key={index} className="relative min-w-48">
                        <FilterIcon className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                        <select
                          value={filter.value}
                          onChange={filter.onChange}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-blue-500 focus:border-blue-500"
                        >
                          {filter.options.map((option, optIndex) => (
                            <option key={optIndex} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  } else if (filter.type === 'custom') {
                    return <div key={index}>{filter.component}</div>;
                  }
                  return null;
                })}
              </div>

              {(toolbarActions.length > 0 || onToggleStats || onRefresh) && (
                <div className="flex items-center space-x-1">
                  {toolbarActions.map((action, index) => (
                    <ActionButton
                      key={index}
                      icon={action.icon}
                      onClick={action.onClick}
                      variant={action.variant || 'secondary'}
                      tooltip={action.tooltip}
                    />
                  ))}

                  {onToggleStats && (
                    <ActionButton
                      icon={BarChart3}
                      onClick={onToggleStats}
                      variant={showStats ? 'primary' : 'secondary'}
                      tooltip={showStats ? 'Hide Stats' : 'Show Stats'}
                    />
                  )}

                  {onRefresh && (
                    <ActionButton
                      icon={RefreshCw}
                      onClick={onRefresh}
                      variant="secondary"
                      tooltip="Refresh"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="p-4">
            <ErrorAlert error={error} variant="error" />
          </div>
        )}

        {success && (
          <div className="p-4">
            <ErrorAlert error={success} variant="success" />
          </div>
        )}

        {children}
      </Card>
    </div>
  );
};

export const Table = ({ columns, data, onRowClick, rowKey = 'id', className = '', sortField, sortDir, onSort }) => {
  const SortIndicator = ({ field }) => {
    if (!onSort) return null;
    if (sortField !== field) return <ChevronsUpDown className="w-3 h-3 ml-1 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 ml-1 inline text-blue-500" />
      : <ChevronDown className="w-3 h-3 ml-1 inline text-blue-500" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${className}`}>
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                onClick={() => column.sortable && onSort && onSort(column.accessor)}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider
                  ${column.sortable && onSort ? 'cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none' : ''}
                  ${column.mobileHidden ? 'hidden sm:table-cell' : ''}
                  ${column.className || ''}`}
              >
                {column.header}
                {column.sortable && <SortIndicator field={column.accessor} />}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, rowIndex) => (
            <tr
              key={row[rowKey] || rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              className={onRowClick ? 'hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer' : ''}
            >
              {columns.map((column, colIndex) => (
                <td key={colIndex} className={`px-6 py-4 whitespace-nowrap text-gray-900 dark:text-gray-100
                  ${column.mobileHidden ? 'hidden sm:table-cell' : ''}
                  ${column.cellClassName || ''}`}>
                  {column.render ? column.render(row) : row[column.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No data available</p>
        </div>
      )}
    </div>
  );
};

export const DataList = ({ data = [], renderItem, emptyState, columns = 1, className = '' }) => {
  if (data.length === 0 && emptyState) {
    return emptyState;
  }

  const gridClassName = columns === 2
    ? 'grid grid-cols-1 md:grid-cols-2 gap-6'
    : columns === 3
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
    : 'space-y-0 divide-y divide-gray-200';

  if (columns > 1) {
    return (
      <div className={`${gridClassName} ${className}`}>
        {data.map((item, index) => (
          <div key={item.id || index} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={`divide-y divide-gray-200 dark:divide-gray-700 ${className}`}>
      {data.map((item, index) => (
        <div key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
          {renderItem(item, index)}
        </div>
      ))}
      {data.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-500 dark:text-gray-400">No items available</p>
        </div>
      )}
    </div>
  );
};

export default DataTable;
